'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import Image from 'next/image';
import BaseCard, { Meta } from '@/components/atoms/BaseCard';
import { haversineMiles, getDirectionsUrl } from '@/shared/geo';
import LocationPin from 'assets/icons/location_pin.svg';
import BrushPin from 'assets/icons/brush.svg';
import ClockMiles from 'assets/icons/clock.svg';
import DirectionsIcon from 'assets/icons/directions.svg';
import AttractionCard from '@/components/attractions/AttractionCard';
import EventCard from '@/components/events/EventCard';
import PromotionCard from '@/components/promotions/PromotionCard';
import PromotionDetails from '@/components/promotions/PromotionDetails';
import MapPin from 'assets/icons/map-pin.svg';
import { formatLocationName } from '@/shared/text';
import { trackExploreEntityClick } from '@/lib/client/gtm';
import HeartSaveButton from '@/components/organisms/HeartSaveButton';
import { TypeBadge } from './markerIcons';
import { useExplore } from './ExploreProvider';
import { MARKER_TYPES } from '../constants';

/**
 * Calculate distance in miles if user location is available
 */
function getDistanceMiles(itemLocation, userLocation) {
  if (!userLocation || !itemLocation) return null;
  const userCoords = [userLocation.lat, userLocation.lng];
  const itemCoords = [itemLocation.lat, itemLocation.lng];
  const dist = haversineMiles(userCoords, itemCoords);
  return Number.isFinite(dist) ? Math.round(dist) : null;
}

/**
 * Heart card for list view
 * Shows: Artist name, location, type badge, Directions, distance (if location available)
 */
function HeartListCard(t0) {
  const $ = _c(65);
  const {
    item,
    userLocation,
    source,
    view,
    isActive: t1,
    onMapClick
  } = t0;
  const isActive = t1 === undefined ? false : t1;
  const {
    heartsScope
  } = useExplore();
  const showActionArea = view !== "map";
  const metasDirection = view === "map" ? "row" : "responsive";
  const slug = item.slug;
  const href = `/hearts/${encodeURIComponent(String(slug))}`;
  let directionsUrl;
  let handleCardClick;
  let handleDirectionsClick;
  let heartBadgeType;
  let leftContent;
  let t2;
  let t3;
  let t4;
  if ($[0] !== heartsScope || $[1] !== item || $[2] !== onMapClick || $[3] !== source || $[4] !== userLocation || $[5] !== view) {
    const distanceMiles = getDistanceMiles(item.location, userLocation);
    heartBadgeType = heartsScope === "saved" ? MARKER_TYPES.SAVED_HEART : heartsScope === "visited" ? MARKER_TYPES.VISITED_HEART : item.type;
    let t5;
    if ($[14] !== item || $[15] !== onMapClick || $[16] !== source || $[17] !== view) {
      t5 = e => {
        trackExploreEntityClick({
          entityType: item.type,
          entityId: item.id,
          entityName: item.name,
          entitySlug: item.slug,
          source,
          view
        });
        if (onMapClick) {
          e?.preventDefault();
          onMapClick(item);
        }
      };
      $[14] = item;
      $[15] = onMapClick;
      $[16] = source;
      $[17] = view;
      $[18] = t5;
    } else {
      t5 = $[18];
    }
    handleCardClick = t5;
    let t6;
    if ($[19] !== item.location || $[20] !== item.name) {
      t6 = item?.location?.lat && item?.location?.lng ? getDirectionsUrl(item.location.lat, item.location.lng, item.name) : null;
      $[19] = item.location;
      $[20] = item.name;
      $[21] = t6;
    } else {
      t6 = $[21];
    }
    directionsUrl = t6;
    let t7;
    if ($[22] !== directionsUrl) {
      t7 = event => {
        event.preventDefault();
        event.stopPropagation();
        if (directionsUrl) {
          window.open(directionsUrl, "_blank", "noopener,noreferrer");
        }
      };
      $[22] = directionsUrl;
      $[23] = t7;
    } else {
      t7 = $[23];
    }
    handleDirectionsClick = t7;
    const imageWrapperClass = view === "map" ? "lg:min-h-[90px] lg:w-[100px] w-[100px] h-[90px]" : "lg:min-h-[104px] lg:h-full lg:w-[98px] w-[100] h-[90px]";
    const imageWidth = view === "map" ? 100 : 98;
    const imageHeight = view === "map" ? 90 : 104;
    const t8 = `${imageWrapperClass} overflow-hidden rounded-lg relative mb-auto lg:mb-0 bg-[#e0e0e0]`;
    let t9;
    if ($[24] !== imageHeight || $[25] !== imageWidth || $[26] !== item.name || $[27] !== item.thumbnail) {
      t9 = item.thumbnail ? <Image src={item.thumbnail} alt={item.name || ""} width={imageWidth} height={imageHeight} sizes="110px" className="w-full h-full object-cover absolute inset-0 text-[14px]" /> : <Image src="/images/heart.jpg" alt={item.name || ""} width={imageWidth} height={imageHeight} sizes="110px" className="w-full h-full object-cover absolute inset-0 text-[14px]" />;
      $[24] = imageHeight;
      $[25] = imageWidth;
      $[26] = item.name;
      $[27] = item.thumbnail;
      $[28] = t9;
    } else {
      t9 = $[28];
    }
    let t10;
    if ($[29] !== t8 || $[30] !== t9) {
      t10 = <div className={t8}>{t9}</div>;
      $[29] = t8;
      $[30] = t9;
      $[31] = t10;
    } else {
      t10 = $[31];
    }
    leftContent = t10;
    if ($[32] !== item.artist) {
      t2 = item.artist && <Meta iconSrc={BrushPin} alt="artist" key="artist">by {item.artist}</Meta>;
      $[32] = item.artist;
      $[33] = t2;
    } else {
      t2 = $[33];
    }
    if ($[34] !== item.communityName || $[35] !== item.locationName) {
      t3 = (item.locationName || item.communityName) && <Meta iconSrc={LocationPin} alt="location" key="loc">{formatLocationName(item.locationName || item.communityName)}</Meta>;
      $[34] = item.communityName;
      $[35] = item.locationName;
      $[36] = t3;
    } else {
      t3 = $[36];
    }
    t4 = Number.isFinite(distanceMiles) && <Meta iconSrc={ClockMiles} width={13} height={10} className="w-3.5 h-auto" alt="distance" key="dist">{distanceMiles} miles away</Meta>;
    $[0] = heartsScope;
    $[1] = item;
    $[2] = onMapClick;
    $[3] = source;
    $[4] = userLocation;
    $[5] = view;
    $[6] = directionsUrl;
    $[7] = handleCardClick;
    $[8] = handleDirectionsClick;
    $[9] = heartBadgeType;
    $[10] = leftContent;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
  } else {
    directionsUrl = $[6];
    handleCardClick = $[7];
    handleDirectionsClick = $[8];
    heartBadgeType = $[9];
    leftContent = $[10];
    t2 = $[11];
    t3 = $[12];
    t4 = $[13];
  }
  const t5 = view === "map" ? "w-10! h-10!" : "lg:w-4! lg:h-4! w-10! h-10!";
  const t6 = view === "map";
  const t7 = view === "map" ? "absolute bottom-1.75 left-2 !text-[#626262]" : "absolute bottom-1.75 left-2 lg:relative lg:bottom-auto lg:left-auto leading-[1.25em] lg:text-[13px] !text-[#626262]";
  let t8;
  if ($[37] !== heartBadgeType || $[38] !== t5 || $[39] !== t6 || $[40] !== t7) {
    t8 = <TypeBadge type={heartBadgeType} key="type" badgeClass={t5} hideLabel={t6} labelClassName="hidden lg:block" className={t7} />;
    $[37] = heartBadgeType;
    $[38] = t5;
    $[39] = t6;
    $[40] = t7;
    $[41] = t8;
  } else {
    t8 = $[41];
  }
  let t9;
  if ($[42] !== t2 || $[43] !== t3 || $[44] !== t4 || $[45] !== t8) {
    t9 = [t2, t3, t4, t8].filter(Boolean);
    $[42] = t2;
    $[43] = t3;
    $[44] = t4;
    $[45] = t8;
    $[46] = t9;
  } else {
    t9 = $[46];
  }
  const metas = t9;
  let t10;
  if ($[47] !== item.id || $[48] !== item.type || $[49] !== view) {
    t10 = view === "map" ? <div className="self-start justify-between min-w-[40px]"><div className="absolute top-3 right-2" onClick={_temp}><HeartSaveButton heartId={item.id} iconOnly={true} hideText={true} isSaved={item.type === MARKER_TYPES.SAVED_HEART || item.type === MARKER_TYPES.SAVED_VISITED_HEART} /></div>{(item.type === MARKER_TYPES.VISITED_HEART || item.type === MARKER_TYPES.SAVED_VISITED_HEART) && <div className="absolute bottom-4 right-2 flex items-center gap-1"><span className="text-xs text-gray-500 font-medium">Visited</span><div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center"><svg xmlns="http://www.w3.org/2000/svg" className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg></div></div>}</div> : null;
    $[47] = item.id;
    $[48] = item.type;
    $[49] = view;
    $[50] = t10;
  } else {
    t10 = $[50];
  }
  let t11;
  if ($[51] !== directionsUrl || $[52] !== handleDirectionsClick) {
    t11 = directionsUrl ? <button type="button" onClick={handleDirectionsClick} className="md:flex hidden items-center gap-3 text-[#323232] cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1CCD83]"><span className="text-sm font-semibold leading-tight">Directions</span><span className="flex h-9 w-9 items-center justify-center rounded-full bg-[linear-gradient(135deg,#73C429,#00EAFD)] p-[1px]"><span className="flex h-full w-full items-center justify-center rounded-full bg-white"><Image src={DirectionsIcon} alt="Directions" className="h-4 w-4" /></span></span></button> : null;
    $[51] = directionsUrl;
    $[52] = handleDirectionsClick;
    $[53] = t11;
  } else {
    t11 = $[53];
  }
  let t12;
  if ($[54] !== handleCardClick || $[55] !== href || $[56] !== isActive || $[57] !== item.name || $[58] !== leftContent || $[59] !== metas || $[60] !== metasDirection || $[61] !== showActionArea || $[62] !== t10 || $[63] !== t11) {
    t12 = <BaseCard href={href} title={item.name} content={leftContent} metas={metas} detailsLabel="Heart Details" onClick={handleCardClick} showActionArea={showActionArea} metasDirection={metasDirection} isActive={isActive} rightColumn={t10} leadingActions={t11} />;
    $[54] = handleCardClick;
    $[55] = href;
    $[56] = isActive;
    $[57] = item.name;
    $[58] = leftContent;
    $[59] = metas;
    $[60] = metasDirection;
    $[61] = showActionArea;
    $[62] = t10;
    $[63] = t11;
    $[64] = t12;
  } else {
    t12 = $[64];
  }
  return t12;
}

/**
 * Attraction card for list view - uses shared AttractionCard component
 * Shows: Address, distance (if location available), type badge
 */
function _temp(e_0) {
  e_0.stopPropagation();
  e_0.preventDefault();
}
function AttractionListCard(t0) {
  const $ = _c(34);
  const {
    item,
    userLocation,
    source,
    view,
    isActive: t1,
    onMapClick
  } = t0;
  const isActive = t1 === undefined ? false : t1;
  const showActionArea = view !== "map";
  const metasDirection = view === "map" ? "row" : "responsive";
  let t2;
  if ($[0] !== item.id) {
    t2 = item.id?.replace("attraction-", "") || item.id;
    $[0] = item.id;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== item.id || $[3] !== item.slug) {
    t3 = item.slug || item.id?.replace("attraction-", "") || item.id;
    $[2] = item.id;
    $[3] = item.slug;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== item.location) {
    t4 = item.location ? [item.location.lat, item.location.lng] : [0, 0];
    $[5] = item.location;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== item.address || $[8] !== item.communityName || $[9] !== item.name || $[10] !== item.thumbnail || $[11] !== t2 || $[12] !== t3 || $[13] !== t4) {
    t5 = {
      id: t2,
      slug: t3,
      title: item.name,
      thumbnail: item.thumbnail,
      address: item.address,
      locationName: item.address,
      locationCoordinates: t4,
      communityName: item.communityName
    };
    $[7] = item.address;
    $[8] = item.communityName;
    $[9] = item.name;
    $[10] = item.thumbnail;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const attractionData = t5;
  let t6;
  if ($[15] !== userLocation) {
    t6 = userLocation ? [userLocation.lat, userLocation.lng] : null;
    $[15] = userLocation;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const userCoords = t6;
  let t7;
  if ($[17] !== item || $[18] !== onMapClick || $[19] !== source || $[20] !== view) {
    t7 = e => {
      trackExploreEntityClick({
        entityType: item.type,
        entityId: item.id,
        entityName: item.name,
        entitySlug: item.slug,
        source,
        view
      });
      if (onMapClick) {
        e?.preventDefault();
        onMapClick(item);
      }
    };
    $[17] = item;
    $[18] = onMapClick;
    $[19] = source;
    $[20] = view;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const handleCardClick = t7;
  const t8 = view === "map" ? "w-10! h-10!" : "lg:w-4! lg:h-4! w-10! h-10!";
  const t9 = view === "map";
  const t10 = view === "map" ? "absolute bottom-1.75 left-2 !text-[#626262]" : "absolute bottom-1.75 left-2 lg:relative lg:bottom-auto lg:left-auto leading-[1.25em] lg:text-[13px] !text-[#626262]";
  let t11;
  if ($[22] !== t10 || $[23] !== t8 || $[24] !== t9) {
    t11 = <TypeBadge type="attraction" badgeClass={t8} hideLabel={t9} labelClassName="hidden lg:block" className={t10} />;
    $[22] = t10;
    $[23] = t8;
    $[24] = t9;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== attractionData || $[27] !== handleCardClick || $[28] !== isActive || $[29] !== metasDirection || $[30] !== showActionArea || $[31] !== t11 || $[32] !== userCoords) {
    t12 = <AttractionCard a={attractionData} coordinates={userCoords} onClick={handleCardClick} showActionArea={showActionArea} metasDirection={metasDirection} isActive={isActive} typeBadge={t11} detailsLabel="Attraction Details" />;
    $[26] = attractionData;
    $[27] = handleCardClick;
    $[28] = isActive;
    $[29] = metasDirection;
    $[30] = showActionArea;
    $[31] = t11;
    $[32] = userCoords;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  return t12;
}

/**
 * Event card for list view - uses shared EventCard component
 * Transforms marker data to match EventCard props
 */
function EventListCard(t0) {
  const $ = _c(29);
  const {
    item,
    source,
    view,
    isActive: t1,
    onMapClick
  } = t0;
  const isActive = t1 === undefined ? false : t1;
  const showActionArea = view !== "map";
  const metasDirection = view === "map" ? "row" : "responsive";
  const firstOccurrence = item.occurances?.[0];
  let t2;
  if ($[0] !== item.id) {
    t2 = item.id?.replace("event-", "") || item.id;
    $[0] = item.id;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== item.id || $[3] !== item.slug) {
    t3 = item.slug || item.id?.replace("event-", "") || item.id;
    $[2] = item.id;
    $[3] = item.slug;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== firstOccurrence || $[6] !== item.communityNames || $[7] !== item.locationName || $[8] !== item.name || $[9] !== t2 || $[10] !== t3) {
    t4 = {
      id: t2,
      slug: t3,
      title: item.name,
      locationName: item.locationName,
      communityNames: item.communityNames,
      firstOccurrence
    };
    $[5] = firstOccurrence;
    $[6] = item.communityNames;
    $[7] = item.locationName;
    $[8] = item.name;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const eventData = t4;
  let t5;
  if ($[12] !== item || $[13] !== onMapClick || $[14] !== source || $[15] !== view) {
    t5 = e => {
      trackExploreEntityClick({
        entityType: item.type,
        entityId: item.id,
        entityName: item.name,
        entitySlug: item.slug,
        source,
        view
      });
      if (onMapClick) {
        e?.preventDefault();
        onMapClick(item);
      }
    };
    $[12] = item;
    $[13] = onMapClick;
    $[14] = source;
    $[15] = view;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const handleCardClick = t5;
  const t6 = firstOccurrence?.date || firstOccurrence;
  const t7 = view === "map" ? "w-10! h-10!" : "lg:w-4! lg:h-4! w-10! h-10!";
  const t8 = view === "map";
  const t9 = view === "map" ? "absolute bottom-1.75 left-2 !text-[#626262]" : "absolute bottom-1.75 left-2 lg:relative lg:bottom-auto lg:left-auto leading-[1.25em] lg:text-[13px] !text-[#626262]";
  let t10;
  if ($[17] !== t7 || $[18] !== t8 || $[19] !== t9) {
    t10 = <TypeBadge type="event" badgeClass={t7} hideLabel={t8} labelClassName="hidden lg:block" className={t9} />;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== eventData || $[22] !== handleCardClick || $[23] !== isActive || $[24] !== metasDirection || $[25] !== showActionArea || $[26] !== t10 || $[27] !== t6) {
    t11 = <EventCard e={eventData} eventDate={t6} onClick={handleCardClick} showActionArea={showActionArea} metasDirection={metasDirection} isActive={isActive} typeBadge={t10} detailsLabel="Event Details" />;
    $[21] = eventData;
    $[22] = handleCardClick;
    $[23] = isActive;
    $[24] = metasDirection;
    $[25] = showActionArea;
    $[26] = t10;
    $[27] = t6;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  return t11;
}

/**
 * Promotion card for list view - uses shared PromotionCard component with modal
 * Transforms marker data to match PromotionCard props
 */
function PromotionListCard(t0) {
  const $ = _c(40);
  const {
    item,
    source,
    view,
    isActive: t1,
    onMapClick
  } = t0;
  const isActive = t1 === undefined ? false : t1;
  const showActionArea = view !== "map";
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPromotion, setSelectedPromotion] = useState(null);
  let t2;
  if ($[0] !== item.id) {
    t2 = item.id?.replace("promotion-", "") || item.id;
    $[0] = item.id;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== item.locations) {
    t3 = item.locations || [];
    $[2] = item.locations;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== item.communityName || $[5] !== item.endDate || $[6] !== item.locationName || $[7] !== item.name || $[8] !== item.offerDetails || $[9] !== item.offerText || $[10] !== item.startDate || $[11] !== item.thumbnail || $[12] !== item.url || $[13] !== item.vendor || $[14] !== item.vendorColor || $[15] !== t2 || $[16] !== t3) {
    t4 = {
      id: t2,
      title: item.name,
      offerText: item.offerText,
      offerDetails: item.offerDetails,
      vendor: item.vendor,
      vendorColor: item.vendorColor,
      thumbnail: item.thumbnail,
      url: item.url,
      startDate: item.startDate,
      endDate: item.endDate,
      locations: t3,
      locationName: item.locationName,
      communityName: item.communityName
    };
    $[4] = item.communityName;
    $[5] = item.endDate;
    $[6] = item.locationName;
    $[7] = item.name;
    $[8] = item.offerDetails;
    $[9] = item.offerText;
    $[10] = item.startDate;
    $[11] = item.thumbnail;
    $[12] = item.url;
    $[13] = item.vendor;
    $[14] = item.vendorColor;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  const promotionData = t4;
  let t5;
  if ($[18] !== item || $[19] !== onMapClick || $[20] !== source || $[21] !== view) {
    t5 = promotion => {
      trackExploreEntityClick({
        entityType: item.type,
        entityId: item.id,
        entityName: item.name || item.offerText || item.title,
        entitySlug: item.slug,
        promotionVendor: item.vendor,
        promotionUrl: item.url,
        source,
        view
      });
      if (onMapClick) {
        onMapClick(item);
        return;
      }
      setSelectedPromotion(promotion);
      setIsModalOpen(true);
      const url = new URL(window.location.href);
      const promotionId = item.promotionId || item.id?.replace(/^promotion-/, "").replace(/-loc-\d+$/, "");
      url.searchParams.set("promoid", promotionId);
      window.history.pushState({}, "", url.toString());
    };
    $[18] = item;
    $[19] = onMapClick;
    $[20] = source;
    $[21] = view;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  const handleOpen = t5;
  let t6;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setIsModalOpen(false);
      setSelectedPromotion(null);
      const url_0 = new URL(window.location.href);
      url_0.searchParams.delete("promoid");
      const newUrl = url_0.search ? `${url_0.pathname}${url_0.search}` : url_0.pathname;
      window.history.pushState({}, "", newUrl);
    };
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  const handleClose = t6;
  const communityName = item.communityName || item.locationName || "";
  const t7 = view === "map" ? "w-7! h-7!" : "lg:w-4! lg:h-4! w-10! h-10!";
  const t8 = view === "map" ? "!text-[#626262]" : "lg:relative lg:bottom-auto lg:left-auto leading-[1.25em] lg:text-[13px] !text-[#626262]";
  let t9;
  if ($[24] !== t7 || $[25] !== t8) {
    t9 = <TypeBadge type="promotion" badgeClass={t7} labelClassName="hidden lg:block" className={t8} />;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== handleOpen || $[28] !== isActive || $[29] !== promotionData || $[30] !== showActionArea || $[31] !== t9) {
    t10 = <PromotionCard promotion={promotionData} onClick={handleOpen} showActionArea={showActionArea} isActive={isActive} detailsLabel="Promotion Details" typeBadge={t9} />;
    $[27] = handleOpen;
    $[28] = isActive;
    $[29] = promotionData;
    $[30] = showActionArea;
    $[31] = t9;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== communityName || $[34] !== isModalOpen || $[35] !== selectedPromotion) {
    t11 = isModalOpen && <PromotionDetails isOpen={isModalOpen} onClose={handleClose} communityName={communityName} promotion={selectedPromotion} />;
    $[33] = communityName;
    $[34] = isModalOpen;
    $[35] = selectedPromotion;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  let t12;
  if ($[37] !== t10 || $[38] !== t11) {
    t12 = <>{t10}{t11}</>;
    $[37] = t10;
    $[38] = t11;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  return t12;
}

/**
 * Community card for list view
 * Shows: Location, type badge
 */
function CommunityListCard(t0) {
  const $ = _c(42);
  const {
    item,
    userLocation,
    source,
    view,
    isActive: t1,
    onMapClick
  } = t0;
  const isActive = t1 === undefined ? false : t1;
  const showActionArea = view !== "map";
  const metasDirection = view === "map" ? "row" : "responsive";
  let t2;
  if ($[0] !== item.id || $[1] !== item.slug) {
    t2 = item.slug || item.id?.replace("community-", "") || item.id;
    $[0] = item.id;
    $[1] = item.slug;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const slug = t2;
  const href = `/community/${encodeURIComponent(String(slug))}`;
  let handleCardClick;
  let leftContent;
  let t3;
  let t4;
  if ($[3] !== item || $[4] !== onMapClick || $[5] !== source || $[6] !== userLocation || $[7] !== view) {
    const distanceMiles = getDistanceMiles(item.location, userLocation);
    let t5;
    if ($[12] !== item || $[13] !== onMapClick || $[14] !== source || $[15] !== view) {
      t5 = e => {
        trackExploreEntityClick({
          entityType: item.type,
          entityId: item.id,
          entityName: item.name,
          entitySlug: item.slug,
          source,
          view
        });
        if (onMapClick) {
          e?.preventDefault();
          onMapClick(item);
        }
      };
      $[12] = item;
      $[13] = onMapClick;
      $[14] = source;
      $[15] = view;
      $[16] = t5;
    } else {
      t5 = $[16];
    }
    handleCardClick = t5;
    const imageWrapperClass = view === "map" ? "lg:min-h-[90px] lg:w-[100px] w-[100px] h-[90px]" : "lg:min-h-[104px] lg:h-full lg:w-[98px] w-[100px] h-[90px]";
    const t6 = `${imageWrapperClass} overflow-hidden rounded-lg relative mb-auto lg:mb-0 bg-[#e0e0e0]`;
    let t7;
    if ($[17] !== item.name || $[18] !== item.thumbnail) {
      t7 = item.thumbnail ? <Image src={item.thumbnail} alt={item.name || ""} width={98} height={104} sizes="110px" className="h-full w-full object-cover absolute inset-0 text-[14px]" /> : <Image src="/images/community.jpg" alt={item.name || ""} width={98} height={104} sizes="110px" className="h-full w-full object-cover absolute inset-0 text-[14px]" />;
      $[17] = item.name;
      $[18] = item.thumbnail;
      $[19] = t7;
    } else {
      t7 = $[19];
    }
    let t8;
    if ($[20] !== t6 || $[21] !== t7) {
      t8 = <div className={t6}>{t7}</div>;
      $[20] = t6;
      $[21] = t7;
      $[22] = t8;
    } else {
      t8 = $[22];
    }
    leftContent = t8;
    if ($[23] !== item.locationName) {
      t3 = item.locationName && <Meta iconSrc={MapPin} alt="location" key="loc">{formatLocationName(item.locationName)}</Meta>;
      $[23] = item.locationName;
      $[24] = t3;
    } else {
      t3 = $[24];
    }
    t4 = Number.isFinite(distanceMiles) && <Meta iconSrc={ClockMiles} width={13} height={10} className="w-3.5 h-auto" alt="distance" key="dist">{distanceMiles} miles away</Meta>;
    $[3] = item;
    $[4] = onMapClick;
    $[5] = source;
    $[6] = userLocation;
    $[7] = view;
    $[8] = handleCardClick;
    $[9] = leftContent;
    $[10] = t3;
    $[11] = t4;
  } else {
    handleCardClick = $[8];
    leftContent = $[9];
    t3 = $[10];
    t4 = $[11];
  }
  const t5 = view === "map" ? "w-10! h-10!" : "lg:w-4! lg:h-4! w-10! h-10!";
  const t6 = view === "map";
  const t7 = view === "map" ? "absolute bottom-1.75 left-1 !text-[#626262]" : "absolute bottom-1.75 left-1 lg:relative lg:bottom-auto lg:left-auto leading-[1.25em] lg:text-[13px] !text-[#626262]";
  let t8;
  if ($[25] !== t5 || $[26] !== t6 || $[27] !== t7) {
    t8 = <TypeBadge type="community" key="type" badgeClass={t5} hideLabel={t6} labelClassName="hidden lg:block" className={t7} />;
    $[25] = t5;
    $[26] = t6;
    $[27] = t7;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== t3 || $[30] !== t4 || $[31] !== t8) {
    t9 = [t3, t4, t8].filter(Boolean);
    $[29] = t3;
    $[30] = t4;
    $[31] = t8;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  const metas = t9;
  let t10;
  if ($[33] !== handleCardClick || $[34] !== href || $[35] !== isActive || $[36] !== item.name || $[37] !== leftContent || $[38] !== metas || $[39] !== metasDirection || $[40] !== showActionArea) {
    t10 = <BaseCard href={href} title={item.name} content={leftContent} metas={metas} detailsLabel="Community Details" onClick={handleCardClick} showActionArea={showActionArea} metasDirection={metasDirection} isActive={isActive} />;
    $[33] = handleCardClick;
    $[34] = href;
    $[35] = isActive;
    $[36] = item.name;
    $[37] = leftContent;
    $[38] = metas;
    $[39] = metasDirection;
    $[40] = showActionArea;
    $[41] = t10;
  } else {
    t10 = $[41];
  }
  return t10;
}

/**
 * Artist card for list view
 * Shows: Location, type badge
 */
function ArtistListCard(t0) {
  const $ = _c(44);
  const {
    item,
    userLocation,
    source,
    view,
    isActive: t1,
    onMapClick
  } = t0;
  const isActive = t1 === undefined ? false : t1;
  const showActionArea = view !== "map";
  const metasDirection = view === "map" ? "row" : "responsive";
  let t2;
  if ($[0] !== item.id || $[1] !== item.slug) {
    t2 = item.slug || item.id?.replace("artist-", "") || item.id;
    $[0] = item.id;
    $[1] = item.slug;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const slug = t2;
  const heartHref = item.heartSlug ? `/hearts/${encodeURIComponent(item.heartSlug)}#artist-spotlight` : null;
  const href = heartHref || `/artist/${encodeURIComponent(String(slug))}`;
  let handleCardClick;
  let leftContent;
  let t3;
  let t4;
  if ($[3] !== item || $[4] !== onMapClick || $[5] !== source || $[6] !== userLocation || $[7] !== view) {
    const distanceMiles = getDistanceMiles(item.location, userLocation);
    let t5;
    if ($[12] !== item || $[13] !== onMapClick || $[14] !== source || $[15] !== view) {
      t5 = e => {
        trackExploreEntityClick({
          entityType: item.type,
          entityId: item.id,
          entityName: item.name,
          entitySlug: item.slug,
          source,
          view
        });
        if (onMapClick) {
          e?.preventDefault();
          onMapClick(item);
        }
      };
      $[12] = item;
      $[13] = onMapClick;
      $[14] = source;
      $[15] = view;
      $[16] = t5;
    } else {
      t5 = $[16];
    }
    handleCardClick = t5;
    const imageWrapperClass = view === "map" ? "lg:min-h-[90px] lg:w-[100px] w-[100px] h-[90px]" : "lg:min-h-[104px] lg:h-full lg:w-[98px] w-[100px] h-[90px]";
    const t6 = `${imageWrapperClass} overflow-hidden rounded-lg relative mb-auto lg:mb-0 bg-[#e0e0e0]`;
    let t7;
    if ($[17] !== item.name || $[18] !== item.thumbnail) {
      t7 = item.thumbnail ? <Image src={item.thumbnail} alt={item.name || ""} width={98} height={104} sizes="110px" className="h-full w-full object-cover absolute inset-0 text-[14px]" /> : <Image src="/images/artist.png" alt={item.name || ""} width={98} height={104} sizes="110px" className="h-full w-full object-cover absolute inset-0 text-[14px]" />;
      $[17] = item.name;
      $[18] = item.thumbnail;
      $[19] = t7;
    } else {
      t7 = $[19];
    }
    let t8;
    if ($[20] !== t6 || $[21] !== t7) {
      t8 = <div className={t6}>{t7}</div>;
      $[20] = t6;
      $[21] = t7;
      $[22] = t8;
    } else {
      t8 = $[22];
    }
    leftContent = t8;
    const t9 = item.communityName || item.locationName;
    let t10;
    if ($[23] !== t9) {
      t10 = formatLocationName(t9);
      $[23] = t9;
      $[24] = t10;
    } else {
      t10 = $[24];
    }
    const locationDisplay = t10;
    if ($[25] !== locationDisplay) {
      t3 = locationDisplay && <Meta iconSrc={LocationPin} alt="location" key="loc">{locationDisplay}</Meta>;
      $[25] = locationDisplay;
      $[26] = t3;
    } else {
      t3 = $[26];
    }
    t4 = Number.isFinite(distanceMiles) && <Meta iconSrc={ClockMiles} width={13} height={10} className="w-3.5 h-auto" alt="distance" key="dist">{distanceMiles} miles away</Meta>;
    $[3] = item;
    $[4] = onMapClick;
    $[5] = source;
    $[6] = userLocation;
    $[7] = view;
    $[8] = handleCardClick;
    $[9] = leftContent;
    $[10] = t3;
    $[11] = t4;
  } else {
    handleCardClick = $[8];
    leftContent = $[9];
    t3 = $[10];
    t4 = $[11];
  }
  const t5 = view === "map" ? "w-10! h-10!" : "lg:w-4! lg:h-4! w-10! h-10!";
  const t6 = view === "map";
  const t7 = view === "map" ? "absolute bottom-1.75 left-2 !text-[#626262]" : "absolute bottom-1.75 left-2 lg:relative lg:bottom-auto lg:left-auto leading-[1.25em] lg:text-[13px] !text-[#626262]";
  let t8;
  if ($[27] !== t5 || $[28] !== t6 || $[29] !== t7) {
    t8 = <TypeBadge type="artist" key="type" badgeClass={t5} hideLabel={t6} labelClassName="hidden lg:block" className={t7} />;
    $[27] = t5;
    $[28] = t6;
    $[29] = t7;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  let t9;
  if ($[31] !== t3 || $[32] !== t4 || $[33] !== t8) {
    t9 = [t3, t4, t8].filter(Boolean);
    $[31] = t3;
    $[32] = t4;
    $[33] = t8;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  const metas = t9;
  let t10;
  if ($[35] !== handleCardClick || $[36] !== href || $[37] !== isActive || $[38] !== item.name || $[39] !== leftContent || $[40] !== metas || $[41] !== metasDirection || $[42] !== showActionArea) {
    t10 = <BaseCard href={href} title={item.name} content={leftContent} metas={metas} detailsLabel="Artist Details" onClick={handleCardClick} showActionArea={showActionArea} metasDirection={metasDirection} isActive={isActive} />;
    $[35] = handleCardClick;
    $[36] = href;
    $[37] = isActive;
    $[38] = item.name;
    $[39] = leftContent;
    $[40] = metas;
    $[41] = metasDirection;
    $[42] = showActionArea;
    $[43] = t10;
  } else {
    t10 = $[43];
  }
  return t10;
}

/**
 * Main ExploreListCard component - routes to entity-specific cards
 * Passes userLocation to enable distance display when location services are active
 */
export default function ExploreListCard(t0) {
  const $ = _c(42);
  const {
    item,
    userLocation,
    source: t1,
    view: t2,
    isActive: t3,
    onMapClick
  } = t0;
  const source = t1 === undefined ? "explore_list" : t1;
  const view = t2 === undefined ? "list" : t2;
  const isActive = t3 === undefined ? false : t3;
  switch (item.type) {
    case MARKER_TYPES.HEART:
    case MARKER_TYPES.VISITED_HEART:
    case MARKER_TYPES.SAVED_HEART:
    case MARKER_TYPES.SAVED_VISITED_HEART:
      {
        let t4;
        if ($[0] !== isActive || $[1] !== item || $[2] !== onMapClick || $[3] !== source || $[4] !== userLocation || $[5] !== view) {
          t4 = <HeartListCard item={item} userLocation={userLocation} source={source} view={view} isActive={isActive} onMapClick={onMapClick} />;
          $[0] = isActive;
          $[1] = item;
          $[2] = onMapClick;
          $[3] = source;
          $[4] = userLocation;
          $[5] = view;
          $[6] = t4;
        } else {
          t4 = $[6];
        }
        return t4;
      }
    case MARKER_TYPES.ATTRACTION:
      {
        let t4;
        if ($[7] !== isActive || $[8] !== item || $[9] !== onMapClick || $[10] !== source || $[11] !== userLocation || $[12] !== view) {
          t4 = <AttractionListCard item={item} userLocation={userLocation} source={source} view={view} isActive={isActive} onMapClick={onMapClick} />;
          $[7] = isActive;
          $[8] = item;
          $[9] = onMapClick;
          $[10] = source;
          $[11] = userLocation;
          $[12] = view;
          $[13] = t4;
        } else {
          t4 = $[13];
        }
        return t4;
      }
    case MARKER_TYPES.EVENT:
      {
        let t4;
        if ($[14] !== isActive || $[15] !== item || $[16] !== onMapClick || $[17] !== source || $[18] !== view) {
          t4 = <EventListCard item={item} source={source} view={view} isActive={isActive} onMapClick={onMapClick} />;
          $[14] = isActive;
          $[15] = item;
          $[16] = onMapClick;
          $[17] = source;
          $[18] = view;
          $[19] = t4;
        } else {
          t4 = $[19];
        }
        return t4;
      }
    case MARKER_TYPES.PROMOTION:
      {
        let t4;
        if ($[20] !== isActive || $[21] !== item || $[22] !== onMapClick || $[23] !== source || $[24] !== view) {
          t4 = <PromotionListCard item={item} source={source} view={view} isActive={isActive} onMapClick={onMapClick} />;
          $[20] = isActive;
          $[21] = item;
          $[22] = onMapClick;
          $[23] = source;
          $[24] = view;
          $[25] = t4;
        } else {
          t4 = $[25];
        }
        return t4;
      }
    case MARKER_TYPES.COMMUNITY:
      {
        let t4;
        if ($[26] !== isActive || $[27] !== item || $[28] !== onMapClick || $[29] !== source || $[30] !== userLocation || $[31] !== view) {
          t4 = <CommunityListCard item={item} userLocation={userLocation} source={source} view={view} isActive={isActive} onMapClick={onMapClick} />;
          $[26] = isActive;
          $[27] = item;
          $[28] = onMapClick;
          $[29] = source;
          $[30] = userLocation;
          $[31] = view;
          $[32] = t4;
        } else {
          t4 = $[32];
        }
        return t4;
      }
    case MARKER_TYPES.ARTIST:
      {
        let t4;
        if ($[33] !== isActive || $[34] !== item || $[35] !== onMapClick || $[36] !== source || $[37] !== userLocation || $[38] !== view) {
          t4 = <ArtistListCard item={item} userLocation={userLocation} source={source} view={view} isActive={isActive} onMapClick={onMapClick} />;
          $[33] = isActive;
          $[34] = item;
          $[35] = onMapClick;
          $[36] = source;
          $[37] = userLocation;
          $[38] = view;
          $[39] = t4;
        } else {
          t4 = $[39];
        }
        return t4;
      }
    default:
      {
        let t4;
        if ($[40] !== item.type) {
          t4 = <div className="bg-white p-4 rounded-lg shadow-sm"><p className="text-sm text-gray-600">Unknown item type: {item.type}</p></div>;
          $[40] = item.type;
          $[41] = t4;
        } else {
          t4 = $[41];
        }
        return t4;
      }
  }
}
export { TypeBadge };